// @file Surface share links store
import { captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { getVuexStore } from '@@/bits/pinia'
import { HttpCode, SnackbarNotificationType } from '@@/enums'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import type { WallSectionBreakoutLink } from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import { GroupByTypes, saveWall } from '@padlet/arvo'
import { ALERT_ICON } from 'bits/confirmation_dialog'
import { OzConfirmationDialogBoxButtonScheme } from 'library/v4/components/OzConfirmationDialogBox.vue'
import { keyBy } from 'lodash-es'
import { defineStore } from 'pinia'
import { useGlobalSnackbarStore } from 'pinia/global_snackbar'
import PadletApi from 'surface/padlet_api'
import { computed, ref } from 'vue'
import { useCopyToClipboard } from 'vuecomposables/copy_to_clipboard'

export const useSurfaceShareLinksStore = defineStore('surfaceShareLinks', () => {
  const surfaceVuexStore = getVuexStore<RootState>() // For gradual conversion to pinia
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const { genericFetchError } = useGlobalSnackbarStore()

  // State
  const isEnablingSectionBreakout = ref(false)
  const isSectionBreakoutEnabled = ref<boolean>(false)
  const sectionBreakoutLinks = ref<Record<number, WallSectionBreakoutLink> | null>(null)
  const disableSectionBreakoutTimer = ref<ReturnType<typeof setTimeout> | undefined>()
  const disableSectionBreakoutSnackbarUid = ref<string | null>(null)

  // Getters
  const canEnableSectionBreakout = computed<boolean>(() => surfaceVuexStore?.getters?.canIAdminister ?? false)
  const xAddSectionButton = computed<boolean>(() => surfaceVuexStore?.getters.canIModerate ?? false)

  // Actions
  async function enableGroupBySections(): Promise<void> {
    try {
      await saveWall({
        ...surfaceVuexStore?.state.wall,
        wish_arrangement: {
          group_by: GroupByTypes.Section,
          is_sectionable: true,
        },
      })
    } catch (e) {
      genericFetchError()
      captureFetchException(e)
    }
  }

  function setSectionBreakoutLinksState(links: WallSectionBreakoutLink[] | null): void {
    if (links !== null) {
      sectionBreakoutLinks.value = keyBy(links, 'wall_section_id')
      isSectionBreakoutEnabled.value = true
    } else {
      isSectionBreakoutEnabled.value = false
      sectionBreakoutLinks.value = null
    }
  }

  async function fetchSectionBreakoutLinks(): Promise<void> {
    try {
      const links = await PadletApi.SectionBreakout.list(surfaceVuexStore?.getters?.wallId)
      sectionBreakoutLinks.value = keyBy(links, 'wall_section_id')
      isSectionBreakoutEnabled.value = true
    } catch (e) {
      if (e.status === HttpCode.NotFound) {
        return
      }
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelListSectionBreakoutLinks' })
    }
  }

  async function enableSectionBreakout(): Promise<void> {
    // Clear any existing callbacks to disable
    if (disableSectionBreakoutTimer.value !== undefined) clearTimeout(disableSectionBreakoutTimer.value)
    globalSnackbarStore.removeSnackbar(disableSectionBreakoutSnackbarUid.value ?? '')

    const links = await PadletApi.SectionBreakout.enable(surfaceVuexStore?.getters?.wallId)
    setSectionBreakoutLinksState(links)
  }

  async function disableSectionBreakout(): Promise<void> {
    await PadletApi.SectionBreakout.disable(surfaceVuexStore?.getters?.wallId)
    setSectionBreakoutLinksState(null)
  }

  async function disableSectionBreakoutWithUndo(): Promise<void> {
    const SNACKBAR_DURATION = 3000

    disableSectionBreakoutSnackbarUid.value = globalSnackbarStore.setSnackbar({
      notificationType: SnackbarNotificationType.success,
      message: __('Section breakout links closed'),
      actionText: 'Undo',
      persist: true,
      actionTextActions: [
        () => {
          // revert and close snackbar
          isSectionBreakoutEnabled.value = true
          if (disableSectionBreakoutTimer.value !== undefined) clearTimeout(disableSectionBreakoutTimer.value)
          globalSnackbarStore.removeSnackbar(disableSectionBreakoutSnackbarUid.value ?? '')
        },
      ],
    })

    // Hide section breakout first to give illusion of deletion
    isSectionBreakoutEnabled.value = false

    // Timeout to close snackbar and disable section breakout links
    disableSectionBreakoutTimer.value = setTimeout(() => {
      globalSnackbarStore.removeSnackbar(disableSectionBreakoutSnackbarUid.value ?? '')
      void disableSectionBreakout()
    }, SNACKBAR_DURATION)
  }

  function askToDisableSectionBreakout(): void {
    void surfaceVuexStore?.dispatch(
      'confirmationDialog/openConfirmationDialog',
      {
        ...ALERT_ICON,
        title: __('Turn off section breakout links?'),
        body: __('Current links will no longer work. Turning links back on will create new links.'),
        confirmButtonText: __('Turn off'),
        cancelButtonText: __('Nevermind'),
        buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
        forceFullWidthButtons: true,
        afterConfirmActions: [disableSectionBreakoutWithUndo],
      },
      { root: true },
    )
  }

  async function copySectionBreakoutLinkToClipboard(sectionId: number): Promise<void> {
    if (sectionBreakoutLinks.value === null) return

    await useCopyToClipboard().copyToClipboard({
      text: sectionBreakoutLinks.value[sectionId].link,
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
      },
    })
  }

  async function toggleSectionBreakout(): Promise<void> {
    const originalValue = isSectionBreakoutEnabled.value
    try {
      // don't allow changes if mid-change
      if (isEnablingSectionBreakout.value) return

      if (isSectionBreakoutEnabled.value) {
        askToDisableSectionBreakout()
      } else {
        // Only show loading state if toggling to True
        isEnablingSectionBreakout.value = true
        await enableSectionBreakout()
        isEnablingSectionBreakout.value = false
      }
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelToggleSectionBreakout' })
      isSectionBreakoutEnabled.value = originalValue
    }
  }

  function showSectionInputDialog(): void {
    const payload = {
      title: __('New section'),
      shouldFadeIn: true,
      inputPlaceholder: 'Section title',
      inputAriaLabel: __('Section title'),
      submitButtonText: 'Create',
      submitActions: [
        async ({ inputValue }) => {
          await surfaceSectionsStore.createSectionWithName(inputValue.trim())
        },
      ],
      validationActions: [
        ({ inputValue }) => {
          void surfaceVuexStore?.dispatch('inputDialog/setSubmitButtonDisable', {
            disable: inputValue.trim().length === 0,
          })
        },
      ],
    }
    void surfaceVuexStore?.dispatch('inputDialog/openInputDialog', payload, { root: true })
  }
  return {
    // State
    isEnablingSectionBreakout,
    isSectionBreakoutEnabled,
    sectionBreakoutLinks,

    // Getters
    canEnableSectionBreakout,
    xAddSectionButton,

    // Actions
    enableGroupBySections,
    toggleSectionBreakout,
    fetchSectionBreakoutLinks,
    copySectionBreakoutLinkToClipboard,
    showSectionInputDialog,
    setSectionBreakoutLinksState,
  }
})
